import { useCallback, useEffect } from 'react'
import { Stack, Text } from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { api, ApiError } from '@/lib/api'
import type { CustomerRow, ProjectUserIdentifierRow, TicketDetailTicket } from '@/types/api'

type CustomerIdentifierForeignUser = {
  user_id: string
  user_name?: string | null
  user_email?: string | null
}

type UseTicketCustomerIdentifiersParams = {
  ticket: TicketDetailTicket | null
  identifiersModalOpen: boolean
  newType: string
  newValue: string
  newDisplay: string
  setNewValue: (value: string) => void
  setNewDisplay: (value: string) => void
  setSaving: (saving: boolean) => void
  setCustomer: (customer: CustomerRow | null) => void
  setRows: (rows: ProjectUserIdentifierRow[]) => void
  refreshThreadSoft: () => Promise<void>
}

export function useTicketCustomerIdentifiers({
  ticket,
  identifiersModalOpen,
  newType,
  newValue,
  newDisplay,
  setNewValue,
  setNewDisplay,
  setSaving,
  setCustomer,
  setRows,
  refreshThreadSoft,
}: UseTicketCustomerIdentifiersParams) {
  const reloadCustomerIdentifiers = useCallback(async () => {
    const projectId = ticket?.project_id
    const customerId = ticket?.customer_id
    if (!projectId || !customerId) return
    try {
      const rows = await api.projectUserIdentifiers(projectId, customerId, { allProjects: true })
      setRows(rows)
    } catch {
      setRows([])
    }
  }, [setRows, ticket?.customer_id, ticket?.project_id])

  const submitNewIdentifier = useCallback(async () => {
    const projectId = ticket?.project_id
    const customerId = ticket?.customer_id
    if (!projectId || !customerId) return
    let value = newValue.trim()
    if (newType === 'email') value = value.toLowerCase()
    if (!value) {
      showNotification({ title: 'Значение', message: 'Укажите значение идентификатора.', color: 'yellow' })
      return
    }

    const openIdentifierMergeModal = (foreign: CustomerIdentifierForeignUser) => {
      const foreignName = foreign.user_name || foreign.user_email || foreign.user_id
      let previewText = ''
      void (async () => {
        try {
          const preview = await api.projectUserIdentifierMergeCustomersPreview(projectId, {
            from_user_id: foreign.user_id,
            to_user_id: customerId,
            identifier_type: newType,
            identifier_value: value,
          })
          previewText = `Будет перенесено: тикетов ${preview.moved_tickets}, идентификаторов ${preview.moved_identifiers}${
            preview.duplicate_identifiers ? ` (дубликатов: ${preview.duplicate_identifiers})` : ''
          }, запросов отзывов ${preview.moved_review_requests}, продуктовых отзывов ${preview.moved_product_review_requests}.`
        } catch {
          previewText = 'Не удалось получить полный предпросмотр переноса, но объединение доступно.'
        }
        openConfirmModal({
          title: 'Пользователь с таким идентификатором уже существует',
          children: (
            <Stack gap={6}>
              <Text size="sm" c="dimmed">
                Идентификатор <Text component="span" fw={600}>{value}</Text> уже добавлен к клиенту{' '}
                <Text component="span" fw={600}>{foreignName}</Text>.
              </Text>
              <Text size="sm" c="dimmed">
                Вы точно хотите добавить этот идентификатор?
              </Text>
              <Text size="sm" c="orange.8" fw={500}>
                При подтверждении карточки клиентов объединятся, и вся история обращений (включая другие проекты)
                будет объединена в текущей карточке.
              </Text>
              <Text size="sm" c="dimmed">
                {previewText}
              </Text>
            </Stack>
          ),
          labels: { confirm: 'Да, объединить и добавить', cancel: 'Отмена' },
          confirmProps: { color: 'orange' },
          onConfirm: () => {
            void (async () => {
              setSaving(true)
              try {
                await api.projectUserIdentifierMergeCustomers(projectId, {
                  from_user_id: foreign.user_id,
                  to_user_id: customerId,
                  identifier_type: newType,
                  identifier_value: value,
                })
                await Promise.all([
                  reloadCustomerIdentifiers(),
                  refreshThreadSoft(),
                  api.customer(customerId).then(setCustomer).catch(() => {}),
                ])
                showNotification({
                  title: 'Карточки объединены',
                  message: 'Клиенты объединены, email привязан к текущей карточке.',
                  color: 'green',
                })
              } catch (error) {
                showNotification({
                  title: 'Ошибка',
                  message: error instanceof ApiError ? error.message : 'Не удалось объединить карточки',
                  color: 'red',
                })
              } finally {
                setSaving(false)
              }
            })()
          },
        })
      })()
    }

    const createIdentifier = async () => {
      setSaving(true)
      try {
        await api.projectUserIdentifierCreate(projectId, {
          user_id: customerId,
          identifier_type: newType,
          identifier_value: value,
          display_name: newType === 'custom' ? newDisplay.trim() || null : null,
        })
        setNewValue('')
        setNewDisplay('')
        await reloadCustomerIdentifiers()
        showNotification({ title: 'Данные клиента', message: 'Идентификатор добавлен.', color: 'green' })
      } catch (error) {
        if (
          error instanceof ApiError &&
          (error.status === 400 || error.status === 409) &&
          /already exists|уже существует|уже добавлен|belongs to another customer|Identifier already exists/i.test(error.message)
        ) {
          try {
            const found = await api.projectUserIdentifierFindUser(projectId, {
              identifier_type: newType,
              identifier_value: value,
              search_all_types: true,
            })
            const foreign = (found || []).find((user) => (user.user_id || '') !== customerId)
            if (foreign?.user_id) {
              openIdentifierMergeModal({
                user_id: foreign.user_id,
                user_name: foreign.user_name,
                user_email: foreign.user_email,
              })
              return
            }
          } catch {
            // fall through to generic error below
          }
        }
        showNotification({
          title: 'Ошибка',
          message: error instanceof ApiError ? error.message : 'Не удалось добавить',
          color: 'red',
        })
      } finally {
        setSaving(false)
      }
    }

    try {
      const found = await api.projectUserIdentifierFindUser(projectId, {
        identifier_type: newType,
        identifier_value: value,
        search_all_types: true,
      })
      const foreign = (found || []).find((user) => (user.user_id || '') !== customerId)
      if (foreign?.user_id) {
        openIdentifierMergeModal({
          user_id: foreign.user_id,
          user_name: foreign.user_name,
          user_email: foreign.user_email,
        })
        return
      }
    } catch {
      // Проверка может временно быть недоступна; пробуем добавить.
      // Если email уже занят, createIdentifier ниже покажет диалог объединения.
    }
    await createIdentifier()
  }, [
    newDisplay,
    newType,
    newValue,
    refreshThreadSoft,
    reloadCustomerIdentifiers,
    setCustomer,
    setNewDisplay,
    setNewValue,
    setSaving,
    ticket?.customer_id,
    ticket?.project_id,
  ])

  const onDeleteIdentifier = useCallback(
    async (identifierId: string) => {
      const projectId = ticket?.project_id
      if (!projectId) return
      try {
        await api.projectUserIdentifierDelete(projectId, identifierId)
        await reloadCustomerIdentifiers()
        showNotification({ title: 'Удалено', message: 'Идентификатор удалён.', color: 'green' })
      } catch (error) {
        showNotification({
          title: 'Ошибка',
          message: error instanceof ApiError ? error.message : 'Не удалось удалить',
          color: 'red',
        })
      }
    },
    [reloadCustomerIdentifiers, ticket?.project_id],
  )

  useEffect(() => {
    if (identifiersModalOpen) void reloadCustomerIdentifiers()
  }, [identifiersModalOpen, reloadCustomerIdentifiers])

  return {
    onDeleteIdentifier,
    reloadCustomerIdentifiers,
    submitNewIdentifier,
  }
}
